import { useState } from 'react';
import { useUserAuth } from '../context/UserAuthContext';
import { Navigate } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

export default function UserLoginPage() {
  const { login, isAuthenticated } = useUserAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/projects/bakrajo-hills" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      // Full reload so AuthContext picks up the admin_token from localStorage
      window.location.replace('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="user-login-page">
      <div className="w-full max-w-sm space-y-8">
        {/* Logo / Title */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
            <Lock className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-light text-white tracking-tight">Bakrajo Hills</h1>
          <p className="text-xs text-white/30 uppercase tracking-[0.2em]">Site Map Access</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 px-4 py-3 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-white/40">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full bg-white/5 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors"
                data-testid="user-login-email"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-white/40">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full bg-white/5 border border-white/10 text-white pl-10 pr-10 py-3 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors"
                data-testid="user-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#C5A059] transition-colors disabled:opacity-50"
            data-testid="user-login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-[10px] text-white/15 uppercase tracking-wider">
          Darwazay Garmyan
        </p>
      </div>
    </div>
  );
}
